import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { AppColors } from "../../constants/AppColors";
import { StatisticsMonthlyCashflowItemResponse } from "../../models/ResponseModels/StatisticsSummaryResponse";
import { formatMoney } from "./statisticsHelpers";

interface AnalyticsCashflowChartProps {
  items: StatisticsMonthlyCashflowItemResponse[];
  currency: string;
  t: (key: string, values?: Record<string, string | number>) => string;
}

const AnalyticsCashflowChart = ({
  items,
  currency,
  t,
}: AnalyticsCashflowChartProps) => {
  const visibleItems = items.slice(-6);
  const maxValue = Math.max(
    ...visibleItems.flatMap((item) => [
      Number(item.revenue || 0),
      Number(item.expenses || 0),
      Math.abs(Number(item.net_profit || 0)),
    ]),
    1,
  );

  const getHeight = (value: number) => {
    return Math.max((Math.abs(value) / maxValue) * 122, 8);
  };

  return (
    <Card sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 950, mb: 2 }}>
          {t("statistics.cashflow")}
        </Typography>

        {visibleItems.length === 0 ? (
          <Typography color="text.secondary">
            {t("statistics.noStatistics")}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                height: 170,
                display: "flex",
                alignItems: "flex-end",
                gap: 1,
              }}
            >
              {visibleItems.map((item) => (
                <Box
                  key={`${item.month}-${item.currency}`}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.4}
                    sx={{ alignItems: "flex-end", height: 130 }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: getHeight(item.revenue),
                        borderRadius: 999,
                        bgcolor: AppColors.primary,
                      }}
                    />

                    <Box
                      sx={{
                        width: 9,
                        height: getHeight(item.expenses),
                        borderRadius: 999,
                        bgcolor: "#f59e0b",
                      }}
                    />

                    <Box
                      sx={{
                        width: 9,
                        height: getHeight(item.net_profit),
                        borderRadius: 999,
                        bgcolor:
                          item.net_profit >= 0 ? "#16a34a" : AppColors.danger,
                      }}
                    />
                  </Stack>

                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: 11,
                      fontWeight: 800,
                      color: AppColors.textMuted,
                    }}
                    noWrap
                  >
                    {item.month}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", mt: 2 }}>
              {[
                [t("statistics.revenue"), AppColors.primary],
                [t("expenses.totalExpenses"), "#f59e0b"],
                [t("statistics.netProfit"), "#16a34a"],
              ].map(([label, color]) => (
                <Stack
                  key={label}
                  direction="row"
                  spacing={0.75}
                  sx={{ alignItems: "center" }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      bgcolor: color,
                    }}
                  />

                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Typography sx={{ mt: 2, fontWeight: 850 }}>
              {t("statistics.latestNetProfit", {
                amount: formatMoney(
                  visibleItems[visibleItems.length - 1]?.net_profit || 0,
                  currency,
                ),
              })}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsCashflowChart;
